import { OpeningHours } from './types';

// Convert address details to title case
export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
};

// Calculate distance between two coordinates in miles (Haversine formula)
export const getDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 3958.8; // Radius of Earth in miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Convert time string to minutes since midnight, ex: 1200 -> 720
export const timeStrToMinutes = (str: string) => {
  if (!str) return null;
  const h = parseInt(str.slice(0, 2), 10);
  const m = parseInt(str.slice(2), 10);
  return h * 60 + m;
};

// Normalize opening hours for easier comparison
export const normalizeOpeningHours = (opening_hours: OpeningHours[]): OpeningHours[][] => {
  const days: OpeningHours[][] = Array(7)
    .fill(null)
    .map(() => []);
  for (const entry of opening_hours) {
    // Narrows an unparsed value to a primitive before using it.
    // oxlint-disable-next-line anti-slop/no-runtime-typeof
    if (typeof entry.day === 'number') {
      days[entry.day].push(entry);
    }
  }
  return days;
};

// Check if the ATM is open 24/7
export const isOpen247 = (opening_hours: OpeningHours[]) => {
  if (!Array.isArray(opening_hours)) return false;
  return [0, 1, 2, 3, 4, 5, 6].every(day =>
    opening_hours.some((h: OpeningHours) => h.day === day && h.open === '0000' && !h.close),
  );
};

// Get the open status of the ATM
/** The human-readable open/closed line shown on an ATM pin. */
interface OpenStatus {
  status: string;
}

export const getOpenStatus = (opening_hours: OpeningHours[]): OpenStatus => {
  if (!opening_hours) return { status: 'Unknown hours' };
  if (isOpen247(opening_hours)) return { status: 'Open' };

  const now = new Date();
  const userDay = now.getDay();
  const userMinutes = now.getHours() * 60 + now.getMinutes();
  const days = normalizeOpeningHours(opening_hours);
  const storeDayArray = days[userDay];

  if (!Array.isArray(storeDayArray) || storeDayArray.length === 0) {
    return { status: 'Closed' };
  }
  for (let i = 0; i < storeDayArray.length; i++) {
    const open = timeStrToMinutes(storeDayArray[i].open);
    let close = timeStrToMinutes(storeDayArray[i].close || '0000');
    if (storeDayArray[i].close === '0000') close = 24 * 60;
    if (open !== null && close !== null) {
      if (userMinutes >= open && userMinutes < close) {
        return { status: 'Open' };
      }
      if (userMinutes < open) {
        return { status: 'Closed' };
      }
    }
  }
  return { status: 'Closed' };
};
